import React from "react";
import { Button as HeadlessButton } from "@headlessui/react";
import { cn } from "@/lib/utils";

export type ButtonVariant =
	| "primary"
	| "secondary"
	| "tertiary"
	| "ghost"
	| "destructive"
	| "outline"
	| "link"
	| "inline";

export type ButtonSize = "sm" | "md" | "lg" | "icon";

export type ButtonShape = "rounded" | "pill" | "circle";

export type ButtonWidth = "inline" | "full";

export interface ButtonProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	shape?: ButtonShape;
	width?: ButtonWidth;
	loading?: boolean;
	leadingIcon?: React.ReactNode;
	trailingIcon?: React.ReactNode;
	iconOnly?: boolean;
	as?: "button" | "a" | "div";
	type?: "button" | "submit" | "reset";
	"aria-expanded"?: boolean;
	href?: string; // For when as="a"
	target?: string; // For when as="a"
	rel?: string; // For when as="a"
}

export const Button = React.forwardRef<
	HTMLButtonElement | HTMLAnchorElement | HTMLDivElement,
	ButtonProps
>(
	(
		{
			variant = "primary",
			size = "md",
			shape = "rounded",
			width = "full", // Default to full width (block) buttons
			loading = false,
			leadingIcon,
			trailingIcon,
			iconOnly = false,
			as = "button",
			type = "button",
			children,
			className = "",
			style,
			disabled,
			"aria-expanded": ariaExpanded,
			href,
			target,
			rel,
			...props
		},
		ref,
	) => {
		const isDisabled = disabled || loading;
		const hasIcon = !!(leadingIcon || trailingIcon || iconOnly);

		// Base classes
		const baseClasses = cn(
			"font-medium transition-all duration-200 border-0",
			"focus:outline-none focus:ring-2 focus:ring-offset-2",
			"disabled:opacity-50 disabled:cursor-not-allowed",
		);

		const usesInlineLayout = variant === "link" || variant === "inline";
		const layoutClasses = usesInlineLayout
			? "inline group"
			: "inline-flex items-center justify-center group min-w-0 text-center flex-nowrap";

		// Width classes
		const widthClasses = {
			inline: "",
			full: "w-full",
		};

		// Size classes
		const sizeClasses = {
			sm: iconOnly
				? "h-8 w-8 p-0"
				: hasIcon
					? "h-9 px-3 py-1.5 text-sm gap-1.5"
					: "h-9 px-4 py-1.5 text-sm",
			md: iconOnly ? "" : "text-base",
			lg: iconOnly
				? "h-12 w-12 p-0"
				: hasIcon
					? "h-14 px-6 py-3 text-lg gap-2.5"
					: "h-14 px-8 py-3 text-lg",
			icon: "h-10 w-10 p-0",
		};

		const sizeStyles: React.CSSProperties =
			size === "md"
				? iconOnly
					? {
							width: "var(--button-height)",
							height: "var(--button-height)",
							padding: 0,
						}
					: {
							height: "var(--button-height)",
							paddingTop: "var(--button-padding-y)",
							paddingBottom: "var(--button-padding-y)",
							paddingLeft: hasIcon
								? "var(--button-padding-x-icon)"
								: "var(--button-padding-x)",
							paddingRight: hasIcon
								? "var(--button-padding-x-icon)"
								: "var(--button-padding-x)",
						}
				: {};

		// Shape classes
		const shapeClasses = {
			rounded: "",
			pill: "rounded-full",
			circle: "rounded-full",
		};

		// Variant classes - using data attributes from Headless UI for state management
		const variantClasses = {
			primary: cn(
				"bg-button-bg-primary text-button-text-primary",
				!loading && "hover:bg-button-bg-primary-hover",
				!loading && "active:bg-button-bg-primary-active",
				"focus:ring-button-focus-ring",
			),
			secondary: cn(
				"bg-button-bg-secondary text-button-text-secondary border border-button-border-secondary",
				!loading && "hover:bg-button-bg-secondary-hover",
				!loading && "active:bg-button-bg-secondary-active",
				"focus:ring-button-focus-ring",
			),
			tertiary: cn(
				"bg-button-bg-tertiary text-button-text-tertiary border border-button-border-tertiary",
				!loading && "hover:bg-button-bg-tertiary-hover",
				!loading && "active:bg-button-bg-tertiary-active",
				"focus:ring-button-focus-ring",
			),
			ghost: cn(
				"bg-button-bg-ghost text-button-text-ghost",
				!loading && "hover:bg-button-bg-ghost-hover",
				!loading && "active:bg-button-bg-ghost-active",
				"focus:ring-button-focus-ring",
			),
			destructive: cn(
				"bg-button-bg-destructive text-button-text-destructive",
				!loading && "hover:bg-button-bg-destructive-hover",
				!loading && "active:bg-button-bg-destructive-active",
				"focus:ring-button-focus-ring",
			),
			outline: cn(
				"text-button-text-outline border-2 border-button-border-outline",
				!loading && "hover:border-button-border-outline-hover",
				!loading && "active:border-button-border-outline-active",
				"focus:ring-button-focus-ring",
			),
			link: cn(
				"bg-transparent text-button-text-link underline underline-offset-4",
				"hover:no-underline",
				"outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0",
				"px-0 py-0 h-auto",
			),
			inline: "bg-transparent p-0 h-auto text-current",
		};

		// Loading spinner component
		const LoadingSpinner = () => (
			<svg
				className="animate-spin h-4 w-4"
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				aria-hidden="true"
			>
				<circle
					className="opacity-25"
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					strokeWidth="4"
				></circle>
				<path
					className="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
				></path>
			</svg>
		);

		// Icon size classes based on button size
		const iconSizeClasses = {
			sm: "h-4 w-4",
			md: "h-5 w-5",
			lg: "h-6 w-6",
			icon: "h-5 w-5",
		};

		const computedWidthClass = variant === "inline" ? "" : widthClasses[width];
		const computedSizeClass = variant === "inline" ? "" : sizeClasses[size];

		const buttonClasses = cn(
			baseClasses,
			layoutClasses,
			computedWidthClass,
			computedSizeClass,
			shapeClasses[shape],
			variantClasses[variant],
			loading && "cursor-wait",
			className,
		);

		const widthStyle: React.CSSProperties =
			size === "md" &&
			width === "inline" &&
			!iconOnly &&
			variant !== "link" &&
			variant !== "inline"
				? { width: "var(--button-width)" }
				: {};

		const buttonStyle: React.CSSProperties = {
			borderRadius:
				shape === "rounded" ? "var(--component-border-radius)" : undefined,
			gap: hasIcon || loading ? "var(--button-gap)" : undefined,
			...(variant === "link"
				? {
						textUnderlineOffset: "4px",
					}
				: {}),
			...widthStyle,
			...(variant === "inline" ? {} : sizeStyles),
			...style,
		};

		const content = (
			<>
				{loading && (
					<span className={cn("flex-shrink-0", iconSizeClasses[size])}>
						<LoadingSpinner />
					</span>
				)}
				{!loading && leadingIcon && (
					<span className={cn("flex-shrink-0", iconSizeClasses[size])}>
						{leadingIcon}
					</span>
				)}
				{!iconOnly && children && (
					<span
						className={cn(
							// When loading, don't allow text to shrink; otherwise use flex-1 for icons or text-center for no icons
							loading
								? "flex-shrink-0 whitespace-nowrap"
								: hasIcon
									? "flex-1 min-w-0 whitespace-nowrap"
									: "text-center w-full whitespace-nowrap",
							variant === "primary"
								? "automint-button-text-primary"
								: "automint-button-text-secondary",
							loading && leadingIcon ? "opacity-0" : "",
						)}
					>
						{children}
					</span>
				)}
				{iconOnly && !loading && children && (
					<span className={cn("flex-shrink-0", iconSizeClasses[size])}>
						{children}
					</span>
				)}
				{!loading && trailingIcon && (
					<span className={cn("flex-shrink-0", iconSizeClasses[size])}>
						{trailingIcon}
					</span>
				)}
			</>
		);

		const anchorRef = ref as React.ForwardedRef<HTMLAnchorElement>;
		const divRef = ref as React.ForwardedRef<HTMLDivElement>;
		const buttonRef = ref as React.ForwardedRef<HTMLButtonElement>;

		// Render as anchor
		if (as === "a") {
			const { type: anchorType, ...anchorAttributes } = (props ??
				{}) as React.AnchorHTMLAttributes<HTMLAnchorElement> & {
				type?: string;
			};
			void anchorType;

			return (
				<HeadlessButton
					as="a"
					ref={anchorRef}
					href={href}
					target={target}
					rel={rel}
					className={buttonClasses}
					disabled={isDisabled}
					aria-disabled={isDisabled}
					aria-busy={loading}
					{...(ariaExpanded !== undefined && { "aria-expanded": ariaExpanded })}
					{...anchorAttributes}
					style={buttonStyle}
				>
					{content}
				</HeadlessButton>
			);
		}

		// Render as div
		if (as === "div") {
			const divAttributes = props as React.HTMLAttributes<HTMLDivElement>;

			return (
				<HeadlessButton
					as="div"
					ref={divRef}
					className={buttonClasses}
					disabled={isDisabled}
					aria-disabled={isDisabled}
					aria-busy={loading}
					{...(ariaExpanded !== undefined && { "aria-expanded": ariaExpanded })}
					{...divAttributes}
					style={buttonStyle}
				>
					{content}
				</HeadlessButton>
			);
		}

		// Default: render as button
		return (
			<HeadlessButton
				ref={buttonRef}
				type={type}
				className={buttonClasses}
				disabled={isDisabled}
				aria-disabled={isDisabled}
				aria-busy={loading}
				{...(ariaExpanded !== undefined && { "aria-expanded": ariaExpanded })}
				{...props}
				style={buttonStyle}
			>
				{content}
			</HeadlessButton>
		);
	},
);

Button.displayName = "Button";
